import { motion, AnimatePresence } from "framer-motion";
import { useEffect } from "react";
import { Zap, Activity } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, YAxis } from "recharts";
import { useLiveEnergy } from "@/lib/liveEnergy";

export default function LiveFeed({ personaKey, baselineScope2, onLiveScope2Change }) {
    const { latest, history, loading, error } = useLiveEnergy(personaKey, 3000);
    const liveAnnual = latest?.annualized_tco2;

    useEffect(() => {
        if (liveAnnual != null && onLiveScope2Change) {
            onLiveScope2Change(liveAnnual);
        }
    }, [liveAnnual, onLiveScope2Change]);

    const delta = liveAnnual != null && baselineScope2
        ? ((liveAnnual - baselineScope2) / baselineScope2) * 100
        : 0;

    const spark = history.map((h, i) => ({ i, v: h.kwh_per_hour }));

    return (
        <div className="bg-white border border-navy/10 p-6 lg:p-7" data-testid="live-feed">
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 bg-emerald-400 dot-live rounded-full"></span>
                    <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-gold">Live · Sample Energy API</span>
                    <span className="hidden md:inline font-mono text-[10px] tracking-[0.22em] uppercase text-navy/40">
                        /api/energy/live?persona={personaKey}
                    </span>
                </div>
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/40">
                    India CEA Grid EF · 0.716 kg / kWh
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Live kWh */}
                <div className="lg:col-span-3">
                    <div className="font-mono text-[9px] tracking-[0.24em] uppercase text-navy/40 mb-2 flex items-center gap-2">
                        <Zap size={11} strokeWidth={1.5} className="text-gold" /> Instant Load
                    </div>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={latest?.kwh_per_hour}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{ duration: 0.35 }}
                            className="font-serif text-4xl text-navy leading-none"
                            data-testid="live-kwh"
                        >
                            {loading ? "—" : latest?.kwh_per_hour?.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                        </motion.div>
                    </AnimatePresence>
                    <div className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-navy/40">kWh / hr</div>
                </div>

                {/* Derived Scope 2 tCO2/hr */}
                <div className="lg:col-span-3">
                    <div className="font-mono text-[9px] tracking-[0.24em] uppercase text-navy/40 mb-2 flex items-center gap-2">
                        <Activity size={11} strokeWidth={1.5} className="text-gold" /> Derived Scope 2
                    </div>
                    <div className="font-serif text-4xl text-navy leading-none" data-testid="live-tco2-hr">
                        {loading ? "—" : latest?.tco2_per_hour?.toFixed(2)}
                    </div>
                    <div className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-navy/40">tCO₂e / hr</div>
                </div>

                {/* Annualized */}
                <div className="lg:col-span-3">
                    <div className="font-mono text-[9px] tracking-[0.24em] uppercase text-navy/40 mb-2">Annualized Scope 2</div>
                    <div className="font-serif text-4xl text-gold leading-none" data-testid="live-annual">
                        {loading ? "—" : liveAnnual?.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                    </div>
                    <div className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-navy/40 flex items-center gap-2">
                        <span>tCO₂e / yr</span>
                        {liveAnnual != null && baselineScope2 && (
                            <span className={delta >= 0 ? "text-red-400" : "text-emerald-400"} data-testid="live-delta">
                                {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}% vs baseline
                            </span>
                        )}
                    </div>
                </div>

                {/* Sparkline */}
                <div className="lg:col-span-3">
                    <div className="font-mono text-[9px] tracking-[0.24em] uppercase text-navy/40 mb-2">Last 12 hours</div>
                    <div className="h-[54px]">
                        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                            <LineChart data={spark}>
                                <YAxis hide domain={["dataMin", "dataMax"]} />
                                <Line type="monotone" dataKey="v" stroke="#C89B2A" strokeWidth={1.6} dot={false} isAnimationActive={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="mt-4 pt-4 border-t border-navy/10 font-mono text-[9px] tracking-[0.22em] uppercase text-navy/40">
                {error ? (
                    <span className="text-red-400">Feed offline · {error}</span>
                ) : (
                    <>
                        Feed → Scope 2 = kWh × 0.716 / 1000 · updated {latest ? new Date(latest.timestamp).toLocaleTimeString("en-IN") : "—"} · Risk charts recompute in real time
                    </>
                )}
            </div>
        </div>
    );
}
